import { InfoOutlined } from "@mui/icons-material";
import { Box, Typography, useTheme } from "@mui/material";
import React from "react";
import { Tooltip } from "../../../../components/Tooltip";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import { useLocalization } from "../../../../hooks/useLocalization";
import type { CountryMetricRow } from "../types";
import { CountryUsageOtherTooltipContent } from "./CountryUsageOtherTooltipContent";
import { CountryUsageProgressBar } from "./CountryUsageProgressBar";

const MIN_METRIC_DISPLAY_THRESHOLD = 5;

interface CountryUsageRowProps {
  row: CountryMetricRow;
  barColor: string;
  isUserLevelDataEnabled?: boolean;
}

export const CountryUsageRow: React.FC<CountryUsageRowProps> = ({
  row,
  barColor,
  isUserLevelDataEnabled = false,
}) => {
  const { t } = useLocalization();
  const theme = useTheme();

  const getDisplayPercentage = () => {
    if (row.percentage === -1) {
      return t("pages.dataAnalytics.internalUsage.countryUsage.notAvailable");
    }
    if (row.percentage === 0) {
      return "<1%";
    }

    return `${row.percentage}%`;
  };

  const displayPercentage = getDisplayPercentage();

  const getDisplayMetricValue = () => {
    if (
      !isUserLevelDataEnabled &&
      row.isOther &&
      row.metricValue < MIN_METRIC_DISPLAY_THRESHOLD
    ) {
      return `<${MIN_METRIC_DISPLAY_THRESHOLD}`;
    }
    if (isUserLevelDataEnabled && row.metricValue > 0 && row.metricValue < 1) {
      return "<1";
    }

    return Math.round(row.metricValue).toLocaleString();
  };

  const displayMetricValue = getDisplayMetricValue();

  const isMobile = useIsMobile();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: "4px",
      }}
    >
      {/* Top row: name, percentage, (optional info icon), session count */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: "4px",
        }}
      >
        {/* Country name */}
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 12,
            lineHeight: "16px",
            letterSpacing: "0.02em",
            color: theme.colors.text[1],
          }}
        >
          {row.name}
        </Typography>

        {/* Percentage */}
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: 12,
            lineHeight: "16px",
            letterSpacing: "0.02em",
            color: theme.colors.text[1],
          }}
        >
          ({displayPercentage})
        </Typography>

        {row.isUnknown && (
          <Tooltip
            message={t(
              "pages.dataAnalytics.internalUsage.countryUsage.unknownTooltip",
            )}
            placement={isMobile ? "bottom" : "right"}
          >
            <InfoOutlined sx={{ fontSize: 16, color: theme.colors.text[1] }} />
          </Tooltip>
        )}

        {/* Info icon for "Other" bucket — tooltip explains the grouping */}
        {row.isOther && (
          <Tooltip
            message={<CountryUsageOtherTooltipContent />}
            placement={isMobile ? "bottom" : "right"}
          >
            <InfoOutlined sx={{ fontSize: 16, color: theme.colors.text[1] }} />
          </Tooltip>
        )}

        {/* Session count — pushed to the right */}
        <Typography
          sx={{
            flex: 1,
            fontWeight: 500,
            fontSize: 14,
            lineHeight: "20px",
            letterSpacing: "0.02em",
            textAlign: "right",
            color: theme.colors.text[5],
          }}
        >
          {displayMetricValue}
        </Typography>
      </Box>

      {/* Progress bar */}
      <CountryUsageProgressBar
        color={barColor}
        percentage={row.percentage === -1 ? 0 : row.percentage}
      />
    </Box>
  );
};
